import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@common/bw-ui/lib/utils';
import { useFormControl } from './form-field';

const inputVariants = cva(
  // data-success is the validated state: consumers set it once the field holds
  // a value that passed validation. It doesn't restyle the field itself — the
  // green check icon overlay the consumer renders is the whole success
  // treatment. aria-invalid (set explicitly or inherited from FormField's
  // error) is the invalidated counterpart and does restyle the field;
  // consumers must not set both at once (suppress success while invalid).
  'tw:flex tw:w-full tw:bg-background tw:file:border-0 tw:file:bg-transparent tw:file:text-sm tw:file:font-medium tw:file:text-foreground tw:placeholder:text-muted tw:focus-visible:outline-none tw:disabled:cursor-not-allowed tw:disabled:opacity-50 tw:aria-[invalid=true]:border-2 tw:aria-[invalid=true]:border-destructive tw:aria-[invalid=true]:bg-destructive-light',
  {
    variants: {
      variant: {
        default:
          'tw:h-10 tw:rounded-md tw:border tw:border-border tw:px-3 tw:py-2 tw:text-sm tw:ring-offset-background tw:focus-visible:ring-2 tw:focus-visible:ring-ring tw:focus-visible:ring-offset-2',
        // Focus is a soft blurred glow that matches the field's border colour
        // (grey normally, red while invalid) rather than a hard focus ring.
        // theme() not var(): consumers import the theme with prefix(tw), which
        // renames the emitted variables, so unprefixed var() refs silently
        // resolve to nothing at runtime.
        // text-base is load-bearing, not cosmetic: Safari zooms the viewport
        // whenever a focused input is under 16px. h-12 keeps the value off the
        // border now that it's 16px.
        form: 'tw:h-12 tw:rounded-lg tw:border tw:border-border-strong tw:px-2.5 tw:py-2 tw:text-base tw:font-normal tw:text-foreground tw:focus-visible:shadow-[0_0_2px_1px_theme(--color-border-strong)] tw:aria-[invalid=true]:focus-visible:shadow-[0_0_2px_1px_theme(--color-destructive)]',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

export interface InputProps
  extends
    React.InputHTMLAttributes<HTMLInputElement>,
    VariantProps<typeof inputVariants> {}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, variant, ...props }, ref) => {
    const controlProps = useFormControl(props);
    return (
      <input
        type={type}
        className={cn(inputVariants({ variant, className }))}
        ref={ref}
        {...props}
        {...controlProps}
      />
    );
  },
);
Input.displayName = 'Input';

export { Input, inputVariants };
